import CountUp from 'react-countup';
import {
  AlertCircle,
  BarChart3,
  Clock,
  CheckCircle2,
  Gauge,
  MinusCircle,
  TrendingUp,
  Users,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import { StatisticsProps } from '@/types';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import useTranslation from '@/hooks/useTranslation';
import {
  translateCompletedInterviews,
  translateCompletionRate,
  translateStatistics,
  translateMedianTime,
  translateAverageTime,
  translateInterviewsInProgress,
  translateRespondentsLabel,
  translateScreeningAbandoned,
  translateScreeningFailed,
  translateScreeningPassRate,
  translateScreeningPassed,
  translateScreeningQuotaInsights,
  translateScreeningTotalScreened,
  translateDataQuality,
  translateQualityExcellent,
  translateQualityGood,
  translateQualityLow,
} from '../project_results/resultsPageTranslations';
import { resolveTranslation } from '@/utils/resolveTranslation';
// TELV1-581 — shared with the Compare table so the two agree by construction.
import { formatDurationMs as formatTime } from '@/utils/formatDurationMs';

interface CompactStatsProps extends StatisticsProps {
  screeningData?: {
    quota: { current: number; max: number; percentage: number };
    screening: {
      total_screened: number;
      passed: number;
      failed: number;
      abandoned: number;
      pass_rate: number;
    };
  } | null;
  isFiltered?: boolean;
  totalCompletedInterviews?: number;
  // TELV1-433 — live data-quality distribution (null/undefined hides the
  // section; the server already returns null on filtered views).
  qualityDistribution?: {
    excellent: number;
    good: number;
    low: number;
    scored: number;
    digested: number;
  } | null;
}

// Hoisted above `CompactStats` so JSX usages don't trip
// @typescript-eslint/no-use-before-define. Pure presentational helpers,
// no behaviour change vs the prior tail-of-file definitions.
function StatRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm text-neutral-600">{label}</span>
      </div>
      <span className="text-sm font-semibold text-neutral-900">{value}</span>
    </div>
  );
}

function StatMini({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-neutral-500">{label}:</span>
      <span className="font-medium text-neutral-700">{value}</span>
    </div>
  );
}

function CompactStats({
  data,
  isLoading,
  screeningData,
  isFiltered,
  totalCompletedInterviews,
  qualityDistribution,
}: CompactStatsProps) {
  const { currentProject: singleProjectData } = useCurrentProject();
  // TELV1-433 — visible labels follow the project's analysis language
  // (resolveTranslation); only the hover tooltip uses the viewer's own
  // locale (lang.get) — the ScreeningInsights split.
  const { lang } = useTranslation();
  const analysisLanguage = singleProjectData?.analysis_language ?? 'English';
  const completed = data?.completed_interviews ?? 0;
  const max = singleProjectData?.respondents_max || 0;
  const started = data?.started_interviews ?? 0;
  const completionRate = data?.completion_rate ?? 0;
  const medianMs = data?.median_time ?? 0;
  const avgMs = data?.average_time ?? 0;

  const showInProgress = started > 0;
  const showCompletionRate = completionRate < 100 && completionRate > 0;

  if (isLoading) {
    return (
      <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5 space-y-4">
        <Skeleton className="h-4 w-24 rounded" />
        <Skeleton className="h-6 w-16 rounded" />
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="h-6 w-20 rounded" />
      </Card>
    );
  }

  if (!data) return null;

  const hasScreening = screeningData && screeningData.screening.total_screened > 0;

  // TELV1-433 — data-quality section, hidden until something is scored.
  const hasQuality = !!qualityDistribution && qualityDistribution.scored > 0;
  const qualitySegments = hasQuality
    ? (
        [
          {
            key: 'excellent',
            color: 'bg-emerald-500',
            count: qualityDistribution.excellent,
          },
          // neutral-500, not lighter: measured at design review — a
          // neutral-300 fill is ~1.5:1 against the card and disappears in
          // the common all-Good case (WCAG 1.4.11 wants 3:1).
          {
            key: 'good',
            color: 'bg-neutral-500',
            count: qualityDistribution.good,
          },
          { key: 'low', color: 'bg-red-500', count: qualityDistribution.low },
        ] as const
      )
        .filter((segment) => segment.count > 0)
        .map((segment) => ({
          ...segment,
          // Floor non-zero segments at 3% so a 1-in-200 Low never rounds
          // to sub-pixel; the exact count is always in the grid below.
          width: Math.max((segment.count / qualityDistribution.scored) * 100, 3),
        }))
    : [];
  const passRate = screeningData?.screening?.pass_rate ?? 0;
  // TELV1-398: brand-pink fill, not a traffic-light. A low pass rate or a
  // reached quota is not an error, so red is misleading. Keep a subtle amber
  // only for genuine over-recruitment (respondents past 100% of the target).
  const passBarColor = 'bg-tellet-pink';
  const quotaPercentage = screeningData?.quota?.percentage ?? 0;
  const quotaBarColor = quotaPercentage > 100 ? 'bg-amber-500' : 'bg-tellet-pink';

  return (
    <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-4 w-4 text-neutral-400" />
        <h3 className="text-sm font-semibold text-neutral-900">
          {resolveTranslation(translateStatistics, analysisLanguage)}
        </h3>
      </div>

      <div className="flex flex-col gap-3">
        {/* Completed interviews */}
        <StatRow
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
          label={resolveTranslation(translateCompletedInterviews, analysisLanguage)}
          value={
            isFiltered && totalCompletedInterviews ? (
              <>
                <CountUp end={completed} />{' '}
                <span className="text-neutral-400 font-normal">of {totalCompletedInterviews}</span>
              </>
            ) : (
              <>
                <CountUp end={completed} /> / {max}
              </>
            )
          }
        />

        {/* In progress — only if > 0 */}
        {showInProgress && (
          <StatRow
            icon={<Users className="h-4 w-4 text-blue-500" />}
            label={resolveTranslation(translateInterviewsInProgress, analysisLanguage)}
            value={<CountUp end={started} />}
          />
        )}

        {/* Completion rate — only if not 100% */}
        {showCompletionRate && (
          <StatRow
            icon={<TrendingUp className="h-4 w-4 text-violet-500" />}
            label={resolveTranslation(translateCompletionRate, analysisLanguage)}
            value={
              <>
                <CountUp end={completionRate} />%
              </>
            }
          />
        )}

        <div className="border-t border-neutral-100" />

        {/* Median time */}
        <StatRow
          icon={<Clock className="h-4 w-4 text-neutral-400" />}
          label={resolveTranslation(translateMedianTime, analysisLanguage)}
          value={formatTime(medianMs)}
        />

        {/* Average time */}
        <StatRow
          icon={<Clock className="h-4 w-4 text-neutral-400" />}
          label={resolveTranslation(translateAverageTime, analysisLanguage)}
          value={formatTime(avgMs)}
        />

        {/* Screening summary */}
        {hasScreening && (
          <>
            <div className="border-t border-neutral-100" />

            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-4 w-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700">
                {resolveTranslation(translateScreeningQuotaInsights, analysisLanguage)}
              </span>
            </div>

            {/* Pass rate bar */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>{resolveTranslation(translateScreeningPassRate, analysisLanguage)}</span>
                <span className="font-medium">{Math.round(passRate)}%</span>
              </div>
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-200/60">
                <div
                  className={`h-full rounded-full transition-all ${passBarColor}`}
                  style={{ width: `${Math.min(passRate, 100)}%` }}
                />
              </div>
            </div>

            {/* Screening numbers */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <StatMini
                icon={<Users className="h-3 w-3 text-blue-500" />}
                label={resolveTranslation(translateScreeningTotalScreened, analysisLanguage)}
                value={screeningData.screening.total_screened}
              />
              <StatMini
                icon={<CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                label={resolveTranslation(translateScreeningPassed, analysisLanguage)}
                value={screeningData.screening.passed}
              />
              <StatMini
                icon={<XCircle className="h-3 w-3 text-red-500" />}
                label={resolveTranslation(translateScreeningFailed, analysisLanguage)}
                value={screeningData.screening.failed}
              />
              <StatMini
                icon={<AlertCircle className="h-3 w-3 text-amber-500" />}
                label={resolveTranslation(translateScreeningAbandoned, analysisLanguage)}
                value={screeningData.screening.abandoned}
              />
            </div>

            {/* Quota progress */}
            {screeningData.quota.max > 0 && (
              <div className="flex flex-col gap-1 mt-1">
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span>{resolveTranslation(translateRespondentsLabel, analysisLanguage)}</span>
                  <span className="font-medium">
                    {screeningData.quota.current} / {screeningData.quota.max}
                  </span>
                </div>
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-200/60">
                  <div
                    className={`h-full rounded-full transition-all ${quotaBarColor}`}
                    style={{ width: `${Math.min(quotaPercentage, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* TELV1-433 — data-quality distribution. Design-reviewed spec:
            last section of the card; hidden when nothing is scored or a
            filter is active (server sends null then); "not scored" lives
            in the untranslated n=X of Y header idiom, never as a fourth
            tile; the LOW count is the signal, so its number is red
            regardless of how thin its bar segment gets. */}
        {hasQuality && (
          <>
            <div className="border-t border-neutral-100" />

            <div className="flex items-center gap-2 mb-1">
              <Gauge className="h-4 w-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700">
                {resolveTranslation(translateDataQuality, analysisLanguage)}
              </span>
              <TooltipWrapper
                text={lang.get('msg.qualityNotScoredTooltip')}
                className="max-w-[320px] text-xs z-[100]"
              >
                <span className="text-xs font-normal text-neutral-500">
                  n={qualityDistribution.scored} of {qualityDistribution.digested}
                </span>
              </TooltipWrapper>
            </div>

            {/* Stacked share-of-scored bar. Non-zero segments floored at
                3% width so a real Low never rounds to sub-pixel. */}
            <div className="relative flex h-2 w-full overflow-hidden rounded-full bg-neutral-200/60">
              {qualitySegments.map((segment) => (
                <div
                  key={segment.key}
                  className={`h-full ${segment.color}`}
                  style={{ width: `${segment.width}%` }}
                />
              ))}
            </div>

            <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs">
              <StatMini
                icon={<CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                label={resolveTranslation(translateQualityExcellent, analysisLanguage)}
                value={qualityDistribution.excellent}
              />
              <StatMini
                icon={<MinusCircle className="h-3 w-3 text-neutral-500" />}
                label={resolveTranslation(translateQualityGood, analysisLanguage)}
                value={qualityDistribution.good}
              />
              {/* LOW is the actionable signal — red digit, legible at any N. */}
              <div className="flex items-center gap-1.5">
                <AlertCircle className="h-3 w-3 text-red-500" />
                <span className="text-neutral-500">
                  {resolveTranslation(translateQualityLow, analysisLanguage)}:
                </span>
                <span className="font-semibold text-red-700">{qualityDistribution.low}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

export default CompactStats;
